import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { BtnBase, ConfirmationDialog } from 'Components';
import { setActiveCertificateNameLists } from 'Services/CertificateNamesSvc';
import { apiErrorMessage } from './CertificateHelpers';

/**
 * Choose which lists are active, all at once.
 *
 * Saves the exact set ticked: every list not ticked is deactivated. Ticking
 * none is allowed — that switches certificates off without deleting a name —
 * but it is confirmed first, because from then on every verification fails.
 */
const ActiveListsModal = ({ open, lists, onClose, onSaved }) => {
    const { t } = useTranslation();
    const [selected, setSelected] = useState([]);
    const [saving, setSaving] = useState(false);
    const [confirmNone, setConfirmNone] = useState(false);

    useEffect(() => {
        if (open) {
            setSelected(lists.filter((list) => list.is_active).map((list) => list.id));
        }
    }, [open, lists]);

    const toggle = (id) =>
        setSelected((previous) => (previous.includes(id) ? previous.filter((x) => x !== id) : [...previous, id]));

    const save = async () => {
        setSaving(true);
        try {
            await setActiveCertificateNameLists(selected);
            toast.success(t('admin.certificateNames.lists.activeSaved', { count: selected.length }));
            onSaved();
        } catch (error) {
            toast.error(apiErrorMessage(error, t('admin.certificateNames.lists.saveFailed')));
        }
        setSaving(false);
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle>{t('admin.certificateNames.lists.manageActiveTitle')}</DialogTitle>
                <DialogContent dividers>
                    <Alert severity="info" className="mb-3">
                        {t('admin.certificateNames.lists.manageActiveHint')}
                    </Alert>
                    <Box className="flex gap-2 pb-2">
                        <BtnBase
                            size="small"
                            variant="outlined"
                            fullWidth={false}
                            onClick={() => setSelected(lists.map((list) => list.id))}
                        >
                            {t('admin.certificateNames.lists.selectAll')}
                        </BtnBase>
                        <BtnBase size="small" variant="outlined" fullWidth={false} onClick={() => setSelected([])}>
                            {t('admin.certificateNames.lists.selectNone')}
                        </BtnBase>
                    </Box>
                    <List dense>
                        {lists.map((list) => (
                            <ListItem key={list.id} disablePadding>
                                <ListItemButton onClick={() => toggle(list.id)}>
                                    <ListItemIcon>
                                        <Checkbox edge="start" checked={selected.includes(list.id)} tabIndex={-1} disableRipple />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <span className="flex items-center gap-2">
                                                {list.name}
                                                {list.is_default && (
                                                    <Chip size="small" label={t('admin.certificateNames.lists.default')} />
                                                )}
                                            </span>
                                        }
                                        secondary={t('admin.certificateNames.lists.namesCount', {
                                            count: list.names_count,
                                            formatted: Number(list.names_count).toLocaleString(),
                                        })}
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <BtnBase variant="text" fullWidth={false} onClick={onClose}>
                        {t('common.cancel')}
                    </BtnBase>
                    <BtnBase
                        fullWidth={false}
                        isLoading={saving}
                        onClick={() => (selected.length === 0 ? setConfirmNone(true) : save())}
                    >
                        {t('common.save')}
                    </BtnBase>
                </DialogActions>
            </Dialog>

            <ConfirmationDialog
                isOpen={confirmNone}
                title={t('admin.certificateNames.lists.noneActiveTitle')}
                text={t('admin.certificateNames.lists.noneActiveText')}
                onReject={() => setConfirmNone(false)}
                onResolve={() => {
                    setConfirmNone(false);
                    save();
                }}
            />
        </>
    );
};

export default ActiveListsModal;
